import axios from "axios";
import configs from "../configs/apiConfigs";
import { BODY_TYPE, METHOD_TYPE, CommonApiOptions } from "../constants/api";
import { commonApi } from "../helpers/api";
import { attachHeader } from "../utils/apiHandler";
import { getCookie } from "../utils/cookies";

const mcAuthHeader = () => {
  const login = getCookie();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${login?.xprAuthUser ?? ''}`,
  };
};

const BASE_URL = `${configs.SOURCE_URL}concierges_driven`;

const buildQuery = (params: Record<string, any>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

export const getUnassignedLeads = (params: {
  page: number;
  record: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/unassigned-leads/?${buildQuery(params)}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getAcceptedLeads = (params: {
  page: number;
  record: number;
  clinicId?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/accepted-leads/?${buildQuery(params)}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getCompletedLeads = (params: {
  page: number;
  record: number;
  clinicId?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/completed-leads/?${buildQuery(params)}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getDisputedLeads = (params: {
  page: number;
  record: number;
  clinicId?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/disputed-leads/?${buildQuery(params)}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getPayClinicList = () => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/pay-clinic-list/`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getRegularClinicList = (domain: string) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/regular-clinic-list/?domain=${domain}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const assignLeadToClinic = (body: { clinicId: string | number; leadId: string | number }) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/assign-lead-to-clinic/`,
    method: METHOD_TYPE.POST,
    body: {
      type: BODY_TYPE.RAW,
      data: body,
    },
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const approveCredit = (body: { leadId: string | number; notes: string }) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/approved-credit/`,
    method: METHOD_TYPE.POST,
    body: {
      type: BODY_TYPE.RAW,
      data: body,
    },
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const denyCredit = (body: { leadId: string | number; notes: string }) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/deny-credit/`,
    method: METHOD_TYPE.POST,
    body: {
      type: BODY_TYPE.RAW,
      data: body,
    },
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getChatCallHistory = (body: {
  clinicId: number | string;
  conciergeLeadId: number | string;
  conversationId: string | number;
}) => {
  const request: CommonApiOptions = {
    url: `${configs.MESSAGE_CENTER_API}payclinic-chat/chat-call-history`,
    method: METHOD_TYPE.POST,
    body: { type: BODY_TYPE.RAW, data: body },
    apiOptions: { headers: mcAuthHeader() },
  };
  return commonApi(request);
};

export const getCallRecordingStream = (params: {
  callId: string | number;
  clinicId: string | number;
}) => {
  const login = getCookie();
  return axios.get(
    `${configs.MESSAGE_CENTER_API}payclinic-chat/call/recording/stream?${buildQuery(params)}`,
    {
      headers: { Authorization: `Bearer ${login?.xprAuthUser ?? ''}` },
      responseType: 'blob',
    }
  );
};

export type TranscriptSentence = {
  speaker: "Patient" | "Clinic";
  text: string;
  startTime: string;
  endTime: string;
};

export type CallTranscriptResponse = {
  transcriptSid: string | null;
  status: string;
  transcript: TranscriptSentence[] | null;
} | null;

export const isTranscriptPending = (data: CallTranscriptResponse): boolean => {
  if (data == null || data.transcript !== null) return false;
  const s = (data.status ?? "").trim().toLowerCase().replace(/_/g, " ");
  return s === "queued" || s === "in progress";
};

export const getCallTranscript = (params: {
  callId: string | number;
  clinicId: string | number;
}): Promise<CallTranscriptResponse> => {
  const login = getCookie();
  return axios
    .get(
      `${configs.MESSAGE_CENTER_API}payclinic-chat/call/transcript?${buildQuery(params)}`,
      { headers: { Authorization: `Bearer ${login?.xprAuthUser ?? ''}` } }
    )
    .then((res) => (res.data?.data ?? null) as CallTranscriptResponse);
};

export const moveToArchived = (body: {
  id: number | string;
  archivedNotes: {
    reason: string;
    archivedBy: string;
    archivedDate: string;
  };
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/move-to-archived/`,
    method: METHOD_TYPE.POST,
    body: {
      type: BODY_TYPE.RAW,
      data: body,
    },
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};

export const getAssignedLeads = (params: {
  page: number;
  record: number;
  clinicId?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
}) => {
  const request: CommonApiOptions = {
    url: `${BASE_URL}/assigned-leads/?${buildQuery(params)}`,
    method: METHOD_TYPE.GET,
    apiOptions: {
      headers: attachHeader(true),
      withCredentials: true,
    },
  };
  return commonApi(request);
};
